import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Package,
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Search,
  Upload,
  X,
  ArrowLeft,
  Store,
  ImagePlus,
  DollarSign,
  Tag,
  Layers,
  Clock
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SellerProducts() {
  const { token } = useAuth();
  const [seller, setSeller] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "general",
    price: "",
    stock_quantity: "",
    photos: [],
    is_active: true
  });

  const fetchData = useCallback(async () => {
    try {
      // Fetch seller profile
      const sellerRes = await axios.get(`${API}/api/sellers/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSeller(sellerRes.data);

      // Fetch seller's products
      const productsRes = await axios.get(`${API}/api/sellers/me/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProducts(productsRes.data);

      // Fetch categories
      const catRes = await axios.get(`${API}/api/shop/categories`);
      setCategories(catRes.data);

    } catch (err) {
      if (err.response?.status === 404) {
        toast.error("Du er ikke registrert som selger");
      } else {
        toast.error("Kunne ikke laste data");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openNewProduct = () => {
    setSelectedProduct(null);
    setFormData({
      name: "",
      description: "",
      category: "general",
      price: "",
      stock_quantity: "",
      photos: [],
      is_active: true
    });
    setEditDialogOpen(true);
  };

  const openEditProduct = (product) => {
    setSelectedProduct(product);
    setFormData({
      name: product.name || "",
      description: product.description || "",
      category: product.category || "general",
      price: product.price?.toString() || "",
      stock_quantity: product.stock_quantity?.toString() || "",
      photos: product.photos || [],
      is_active: product.is_active !== false
    });
    setEditDialogOpen(true);
  };

  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const formDataUpload = new FormData();
    for (let i = 0; i < files.length; i++) {
      formDataUpload.append("files", files[i]);
    }

    try {
      const res = await axios.post(`${API}/api/upload/multiple`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      const newUrls = res.data.urls || res.data.map(r => r.url);
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, ...newUrls]
      }));
      toast.success(`${newUrls.length} bilde(r) lastet opp`);
    } catch (err) {
      toast.error("Kunne ikke laste opp bilder");
    }
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const saveProduct = async () => {
    if (!formData.name || !formData.price) {
      toast.error("Fyll ut navn og pris");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        stock_quantity: parseInt(formData.stock_quantity) || 0
      };

      if (selectedProduct) {
        // Update existing
        await axios.put(`${API}/api/shop/products/${selectedProduct.product_id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Produkt oppdatert!");
      } else {
        // Create new
        await axios.post(`${API}/api/shop/products`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Produkt opprettet!");
      }

      setEditDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke lagre produkt");
    } finally {
      setSaving(false);
    }
  };

  const toggleProductActive = async (product) => {
    try {
      await axios.put(`${API}/api/shop/products/${product.product_id}`, {
        is_active: !product.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(product.is_active ? "Produkt deaktivert" : "Produkt aktivert");
      fetchData();
    } catch (err) {
      toast.error("Kunne ikke oppdatere produkt");
    }
  };

  const confirmDelete = (product) => {
    setSelectedProduct(product);
    setDeleteDialogOpen(true);
  };

  const deleteProduct = async () => {
    if (!selectedProduct) return;
    
    try {
      await axios.delete(`${API}/api/shop/products/${selectedProduct.product_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Produkt slettet");
      setDeleteDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error("Kunne ikke slette produkt");
    }
  };

  const filteredProducts = products.filter(p => 
    p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!seller) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <Card className="bg-[#0A0A0A] border-white/10 max-w-md">
          <CardContent className="p-8 text-center">
            <Store className="w-16 h-16 mx-auto mb-4 text-[#F7931A]" />
            <h2 className="text-xl font-bold text-white mb-2">
              Ikke registrert som selger
            </h2>
            <p className="text-[#94A3B8] mb-6">
              Du må registrere deg som selger først
            </p>
            <Link to="/bli-selger">
              <Button className="bg-[#F7931A] text-white hover:bg-[#F7931A]/80">
                Bli Selger
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Seller exists but pending - can still add products (they'll be draft)
  const isPending = seller.status === "pending";

  return (
    <div className="min-h-screen bg-[#050505] py-8 px-4">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/seller/dashboard">
              <Button variant="ghost" size="icon" className="text-[#94A3B8] hover:text-white">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-['Unbounded'] font-bold text-white">Mine Produkter</h1>
              <p className="text-[#94A3B8]">{seller.company_name} • {seller.seller_code}</p>
            </div>
          </div>
          
          <Button 
            onClick={openNewProduct}
            className="bg-gradient-to-r from-[#F7931A] to-[#FFCD00] text-black font-bold hover:opacity-90"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nytt Produkt
          </Button>
        </div>

        {/* Pending Seller Banner */}
        {isPending && (
          <Card className="bg-gradient-to-r from-yellow-500/10 via-orange-500/10 to-yellow-500/10 border-yellow-500/30">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-yellow-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <h3 className="text-yellow-300 font-semibold mb-1">Søknaden din er under behandling</h3>
                  <p className="text-[#94A3B8] text-sm">
                    Du kan allerede legge til produkter nå! De vil være <span className="text-yellow-400 font-medium">usynlige for kunder</span> inntil 
                    kontoen din er godkjent. Når du er godkjent, blir alle produktene dine automatisk synlige i butikken.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search & Stats */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Søk produkter..."
              className="pl-9 bg-black/50 border-white/10 text-white"
            />
          </div>
          <div className="flex gap-2">
            <Badge className="bg-[#23F7DD]/20 text-[#23F7DD] px-4 py-2">
              {products.filter(p => p.is_active !== false && !p.is_draft).length} Aktive
            </Badge>
            {isPending && (
              <Badge className="bg-yellow-500/20 text-yellow-400 px-4 py-2">
                {products.filter(p => p.is_draft).length} Utkast
              </Badge>
            )}
            <Badge className="bg-white/10 text-[#94A3B8] px-4 py-2">
              {products.filter(p => p.is_active === false && !p.is_draft).length} Inaktive
            </Badge>
          </div>
        </div>

        {/* Products Grid */}
        {filteredProducts.length === 0 ? (
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-12 text-center">
              <Package className="w-16 h-16 mx-auto mb-4 text-[#94A3B8] opacity-50" />
              <h3 className="text-xl font-semibold text-white mb-2">Ingen produkter ennå</h3>
              <p className="text-[#94A3B8] mb-6">
                Legg til ditt første produkt for å begynne å selge på AllStillas Marketplace
              </p>
              <Button 
                onClick={openNewProduct}
                className="bg-[#F7931A] text-white hover:bg-[#F7931A]/80"
              >
                <Plus className="w-4 h-4 mr-2" />
                Legg Til Produkt
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProducts.map(product => (
              <Card 
                key={product.product_id} 
                className={`bg-[#0A0A0A] border-white/10 overflow-hidden ${
                  product.is_active === false ? 'opacity-60' : ''
                }`}
              >
                {/* Product Image */}
                <div className="aspect-square relative bg-white/5">
                  {product.photos && product.photos.length > 0 ? (
                    <img 
                      src={product.photos[0]} 
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-16 h-16 text-[#94A3B8]/30" />
                    </div>
                  )}
                  
                  {/* Status Badge */}
                  {product.is_draft && (
                    <Badge className="absolute top-2 left-2 bg-yellow-500/80 text-black">
                      Utkast
                    </Badge>
                  )}
                  {!product.is_draft && product.is_active === false && (
                    <Badge className="absolute top-2 left-2 bg-red-500/80 text-white">
                      Inaktiv
                    </Badge>
                  )}
                  
                  {/* Stock Badge */}
                  <Badge className={`absolute top-2 right-2 ${
                    product.stock_quantity > 0 ? 'bg-green-500/80' : 'bg-red-500/80'
                  } text-white`}>
                    {product.stock_quantity || 0} på lager
                  </Badge>
                </div>

                <CardContent className="p-4">
                  <p className="text-[#F7931A] text-xs uppercase tracking-wider mb-1">
                    {categories.find(c => c.id === product.category)?.name || product.category}
                  </p>
                  <h3 className="text-white font-semibold mb-2 line-clamp-2">{product.name}</h3>
                  <p className="text-2xl font-bold text-white mb-4">
                    {product.price?.toLocaleString()} NOK
                  </p>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditProduct(product)}
                      className="flex-1 border-white/20 text-white hover:bg-white/10"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Rediger
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => toggleProductActive(product)}
                      className="border-white/20 text-white hover:bg-white/10"
                      title={product.is_active ? "Deaktiver" : "Aktiver"}
                    >
                      {product.is_active !== false ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => confirmDelete(product)}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-[#F7931A]" />
              {selectedProduct ? "Rediger Produkt" : "Nytt Produkt"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Photos */}
            <div>
              <label className="text-sm text-[#94A3B8] mb-2 block flex items-center gap-2">
                <ImagePlus className="w-4 h-4" /> Bilder
              </label>
              <div className="grid grid-cols-4 gap-2 mb-2">
                {formData.photos.map((url, idx) => (
                  <div key={idx} className="relative aspect-square rounded-lg overflow-hidden bg-white/5">
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => removePhoto(idx)}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center"
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ))}
                <label className="aspect-square rounded-lg border-2 border-dashed border-white/20 flex flex-col items-center justify-center cursor-pointer hover:border-[#F7931A]/50 transition-colors">
                  <Upload className="w-6 h-6 text-[#94A3B8] mb-1" />
                  <span className="text-xs text-[#94A3B8]">Last opp</span>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                </label>
              </div>
            </div>

            {/* Name & Category */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-[#94A3B8] mb-1 block flex items-center gap-2">
                  <Tag className="w-4 h-4" /> Produktnavn *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="F.eks. HAKI Systemstillas 2m"
                  className="bg-black/50 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-[#94A3B8] mb-1 block flex items-center gap-2">
                  <Layers className="w-4 h-4" /> Kategori
                </label>
                <Select 
                  value={formData.category} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger className="bg-black/50 border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id} className="text-white">
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="text-sm text-[#94A3B8] mb-1 block">Beskrivelse</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Beskriv produktet..."
                className="bg-black/50 border-white/10 text-white"
                rows={3}
              />
            </div>

            {/* Price & Stock */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-[#94A3B8] mb-1 block flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> Pris (NOK) *
                </label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="1299"
                  className="bg-black/50 border-white/10 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-[#94A3B8] mb-1 block flex items-center gap-2">
                  <Package className="w-4 h-4" /> Antall på lager
                </label>
                <Input
                  type="number"
                  value={formData.stock_quantity}
                  onChange={(e) => setFormData(prev => ({ ...prev, stock_quantity: e.target.value }))}
                  placeholder="10"
                  className="bg-black/50 border-white/10 text-white"
                />
              </div>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              className="border-white/20 text-white"
            >
              Avbryt
            </Button>
            <Button
              onClick={saveProduct}
              disabled={saving}
              className="bg-[#F7931A] text-white hover:bg-[#F7931A]/80"
            >
              {saving ? "Lagrer..." : (selectedProduct ? "Oppdater" : "Opprett")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-400">Slett Produkt</DialogTitle>
          </DialogHeader>
          <p className="text-[#94A3B8]">
            Er du sikker på at du vil slette <span className="text-white font-medium">"{selectedProduct?.name}"</span>? 
            Dette kan ikke angres.
          </p>
          <DialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="border-white/20 text-white"
            >
              Avbryt
            </Button>
            <Button
              onClick={deleteProduct}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              Slett
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
